import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast, ToastContainer } from 'react-toastify'
import { useCookies } from 'react-cookie'

import {
    BrowserRouter as Router,
    Route,
    Switch,
    useHistory,
    useLocation,
} from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { CircularProgress } from '@mui/material'
import { useUserAuthorized } from 'hooks/useUserAuthorized'

// eslint-disable-next-line max-len
import { fetchBillingRequest } from 'containers/billing-information/store/actions'
import { ICustomer } from 'containers/billing-information/store/types'
import PrivateRoute from 'router/component/PrivateRoute'
import constructorRoutes from 'router/routes/constructor'
import dashboardRoutes from 'router/routes/dashboard'
import authRoutes from 'router/routes/auth'
import placementRoutes from 'router/routes/placement'
import ProductTour from 'components/product-tour/ProductTour'
import { fetchSettingsAction } from 'containers/account/store/actions'
import { TError } from 'config/types'
import { RootState } from 'redux/reducers/rootReducer'

import Dashboard from 'layouts/dashboard/Dashboard'
import NewLoaderWrapper from 'layouts/new-loader/NewLoaderWrapper'
import ResetPassword from 'containers/auth/reset-password/ResetPassword'
import ConfirmEmail from 'containers/auth/confirm-email/ConfirmEmail'
import Placement from 'containers/placement/Placement'
// eslint-disable-next-line max-len
import SuccessFirstPayment from 'containers/success-first-payment/SuccessFirstPayment'
import FromWhom from 'containers/from-whom/FromWhom'
import { PATH_ONBOARDING, isPublicAuthPath } from 'config/paths'
import NotFound from './not-found/notFound'
import ScrollToTop from './ScrollToTop'

import 'react-toastify/dist/ReactToastify.css'
import 'styles/index.scss'

import InjectAxiosInterceptors from '../../injectedAxiosInterceptors'

const FirstPaymentRedirect: React.FC = () => {
    const history = useHistory()
    const { settings } = useSelector(
        (state: RootState) => state.accountSettings,
    )

    useEffect(() => {
        if (settings?.isFirstPaymentRedirectApplicable) {
            history.push('/successful-first-payment')
        }
    }, [history, settings?.isFirstPaymentRedirectApplicable])

    return null
}

function AppRoutes(): React.ReactElement {
    const { i18n, t } = useTranslation()
    const currentUser = useUserAuthorized()
    const { pathname } = useLocation()
    const isPublicAuthRoute = isPublicAuthPath(pathname)

    const dispatch = useDispatch()
    const setCookie = useCookies()[1]

    const { settings } = useSelector(
        (state: RootState) => state.accountSettings,
    )

    const [isGettingData, setIsGettingData] = useState(false)
    const settingsRequestedRef = useRef(false)

    useEffect(() => {
        if (!currentUser || isPublicAuthRoute) {
            setIsGettingData(false)
            settingsRequestedRef.current = false
            return
        }

        if (Object.values(settings || {}).length > 0) {
            setIsGettingData(false)
            return
        }

        if (settingsRequestedRef.current) {
            return
        }

        settingsRequestedRef.current = true
        setIsGettingData(true)
        dispatch(
            fetchSettingsAction({
                callback: (type: string, errorArray?: TError[]) => {
                    setIsGettingData(false)
                    if (type === 'error') {
                        settingsRequestedRef.current = false
                        errorArray?.forEach(err => {
                            console.error(err.description)
                            toast.error(t('toast:backendError'))
                        })
                    }
                },
            }),
        )
    }, [dispatch, t, settings, currentUser, isPublicAuthRoute])

    useEffect(() => {
        if (!currentUser || isPublicAuthRoute) {
            return
        }

        dispatch(
            fetchBillingRequest({
                callback: (customer: ICustomer) => {
                    if (customer.subscriptionType) {
                        setCookie('paid_plan', customer.subscriptionType, {
                            path: '/',
                        })
                    }

                    setCookie('credits', customer.availablePoints, {
                        path: '/',
                    })

                    setCookie('isExpired', customer.isSubscriptionExpired, {
                        path: '/',
                    })

                    if (customer.isTrialUsed !== undefined) {
                        setCookie('isTrialUsed', customer.isTrialUsed, {
                            path: '/',
                        })
                    }
                    if (customer.nextPaymentDate) {
                        setCookie(
                            'next_payment_date',
                            customer.nextPaymentDate,
                            {
                                path: '/',
                            },
                        )
                    }

                    if (
                        customer.appliedCoupon &&
                        Number(customer.appliedCoupon?.percentOff) === 100
                    ) {
                        setCookie('next_free', 'true', {
                            path: '/',
                        })
                    }
                },
                lang: i18n.language,
            }),
        )
    }, [dispatch, setCookie, i18n.language, currentUser, isPublicAuthRoute])

    if (isGettingData) {
        return (
            <div className='global-circular-progress'>
                <CircularProgress />
            </div>
        )
    }

    return (
        <>
            <ScrollToTop />
            <ProductTour />
            <React.Suspense fallback={<span />}>
                <InjectAxiosInterceptors />
                <FirstPaymentRedirect />
                <Switch>
                    <Route
                        path='/user/confirm-email/:path?/:path?'
                        component={ConfirmEmail}
                        exact
                    />

                    <Route path='/successful-first-payment'>
                        <Dashboard type='constructor'>
                            <SuccessFirstPayment />
                        </Dashboard>
                    </Route>

                    <Route path={PATH_ONBOARDING.root}>
                        <FromWhom />
                    </Route>

                    <Route
                        path='/recovery-password/:path?/:path?'
                        component={ResetPassword}
                        exact
                    />
                    {authRoutes.map(route => (
                        <Route
                            exact
                            path={route.path}
                            component={route.component}
                            key={route.module + route.id}
                        />
                    ))}
                    <Route path='/placement'>
                        <Placement>
                            {placementRoutes.map(route => (
                                <Route
                                    exact
                                    path={route.path}
                                    component={route.component}
                                    key={route.module + route.id}
                                />
                            ))}
                        </Placement>
                    </Route>
                    <Route path='/dashboard'>
                        <Dashboard type='dashboard'>
                            <React.Suspense fallback={<span />}>
                                <Switch>
                                    <Route path='/dashboard/loader'>
                                        <NewLoaderWrapper />
                                    </Route>
                                    {dashboardRoutes.map(route => (
                                        <Route
                                            exact
                                            path={route.path}
                                            component={route.component}
                                            key={route.module + route.id}
                                        />
                                    ))}
                                </Switch>
                            </React.Suspense>
                        </Dashboard>
                    </Route>
                    <Route path='/:path?/:path?/:path?/:path?' exact>
                        <Dashboard type='constructor'>
                            <React.Suspense fallback={<span />}>
                                <Switch>
                                    {constructorRoutes.map(route => {
                                        if (route.protected) {
                                            return (
                                                <PrivateRoute
                                                    exact
                                                    path={route.path}
                                                    component={route.component}
                                                    key={
                                                        route.module + route.id
                                                    }
                                                />
                                            )
                                        }
                                        return (
                                            <Route
                                                exact
                                                path={route.path}
                                                component={route.component}
                                                key={route.module + route.id}
                                            />
                                        )
                                    })}
                                </Switch>
                            </React.Suspense>
                        </Dashboard>
                    </Route>
                    <Route component={NotFound} />
                </Switch>
                <ToastContainer position='bottom-center' theme='dark' />
            </React.Suspense>
        </>
    )
}

function App(): React.ReactElement {
    const { i18n } = useTranslation()

    useEffect(() => {
        const script = document.createElement('script')
        const bwcWrap = document?.querySelectorAll<HTMLElement>('#bwc-wrap')

        if (bwcWrap) {
            bwcWrap?.forEach(e => {
                if (!e?.classList?.contains('used-chat')) {
                    e?.classList.add('used-chat')
                    e?.classList.add(`${i18n.language}`)
                }
            })

            bwcWrap?.forEach(e => {
                if (e?.classList?.contains(`${i18n.language}`)) {
                    e.style.display = 'none'
                } else {
                    e.style.display = 'block'
                }
            })
        }
        /* eslint-disable max-len */
        script.innerHTML =
            i18n.language === 'en'
                ? "(function(d, w, s) {var widgetHash = 'ZmNOuzlAkKQkWreZxcS2', bch = d.createElement(s); bch.type = 'text/javascript'; bch.async = true; bch.src = '//widgets.binotel.com/chat/widgets/' + widgetHash + '.js'; var sn = d.getElementsByTagName(s)[0]; sn.parentNode.insertBefore(bch, sn);})(document, window, 'script');"
                : "(function(d, w, s) { var widgetHash = 'FkQP5UiY1y8PdOjxbGEb', bch = d.createElement(s); bch.type = 'text/javascript'; bch.async = true; bch.src = '//widgets.binotel.com/chat/widgets/' + widgetHash + '.js'; var sn = d.getElementsByTagName(s)[0]; sn.parentNode.insertBefore(bch, sn);})(document, window, 'script');"

        script.id = 'chat-script'
        script.type = 'text/javascript'
        script.async = true
        script.defer = true

        document.body.appendChild(script)
    }, [i18n.language])

    return (
        <Router
            getUserConfirmation={() => {
                /* Empty callback to block the default browser prompt */
            }}
        >
            <AppRoutes />
        </Router>
    )
}

export default App
